import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import DefaultText from '../common/DefaultText';
import FileInputButton from '../common/FileInputButton';
import DefaultImageView from '../common/DefaultImageView';
import {useFormatMsg} from 'utils/useFormatMsg';
import {ICognitoType, IFile} from 'interfaces/interface';
import {useRecoilValue} from 'recoil';
import {userInfoState} from 'utils/Atoms';
import {
  convertPdfToImage,
  convertThumbnailFile,
  handleDownloadClick,
} from 'function/converter';
import CommonModal from 'components/common/CommonModal';
import {s3GetCognito} from 'utils/cognito';
// import ChangeFileNameModal from './ChangeFileNameModal';

/**
 * 라벨 문서 컴포넌트의 Props 인터페이스
 * @property {any} [labelText] - 라벨 텍스트
 * @property {string} [warningText] - 경고 텍스트
 * @property {any[]} list - 문서 목록
 * @property {any} [setList] - 목록 설정 함수
 * @property {string} [inputAccept] - 입력 허용 파일 타입
 * @property {number} [listMaxLength] - 목록 최대 길이
 * @property {string} [buttonText] - 버튼 텍스트
 * @property {React.CSSProperties} [inputStyle] - 입력 스타일
 * @property {React.CSSProperties} [labelStyle] - 라벨 스타일
 * @property {React.CSSProperties} [labelInputContainerStyle] - 라벨 입력 컨테이너 스타일
 * @property {React.CSSProperties} [labelTextStyle] - 라벨 텍스트 스타일
 * @property {React.CSSProperties} [inputWrapStyle] - 입력 래퍼 스타일
 * @property {React.CSSProperties} [buttonStyle] - 버튼 스타일
 * @property {React.CSSProperties} [buttonTextStyle] - 버튼 텍스트 스타일
 * @property {React.CSSProperties} [listWrapStyle] - 목록 래퍼 스타일
 * @property {React.CSSProperties} [itemWrapStyle] - 항목 래퍼 스타일
 * @property {boolean} [readOnly] - 읽기 전용 여부
 * @property {ICognitoType} [type] - Cognito 타입
 * @property {string} [uuid] - UUID
 * @property {boolean} [edit] - 편집 가능 여부
 * @property {boolean} [download] - 다운로드 가능 여부
 */
interface ILabelInputProps {
  labelText?: any;
  warningText?: string;
  list: any[];
  setList?: any;
  inputAccept?: string;
  listMaxLength?: number;
  buttonText?: string;
  inputStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  labelInputContainerStyle?: React.CSSProperties;
  labelTextStyle?: React.CSSProperties;
  inputWrapStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  buttonTextStyle?: React.CSSProperties;
  listWrapStyle?: React.CSSProperties;
  itemWrapStyle?: React.CSSProperties;
  readOnly?: boolean;
  type?: ICognitoType;
  uuid?: string;
  edit?: boolean;
  download?: boolean;
}

/**
 * 라벨 문서 컴포넌트
 * 문서 업로드 및 관리 기능을 제공하는 컴포넌트입니다.
 * @param {ILabelInputProps} props - 라벨 문서 컴포넌트의 props
 * @returns {JSX.Element} 라벨 문서 컴포넌트
 */
const LabelDocument = ({
  labelText,
  warningText,
  setList,
  list,
  listMaxLength = 10,
  buttonText,
  inputAccept,
  readOnly,
  type = 'users',
  uuid,
  edit,
  download,
}: ILabelInputProps) => {
  const label = useFormatMsg(labelText) || labelText;
  const userInfo = useRecoilValue(userInfoState);

  const [deleteDocument, setDeleteDocument] = useState<string>('');
  const [editDocument, setEditDocument] = useState<null | {
    item: File | IFile;
    index: number;
  }>(null);

  const handleAddFileClick = async (file: File) => {
    if (list.length < listMaxLength) {
      setList([...list, file]);
      // const newFile = (
      //   file
      //     ? file.type.includes('pdf')
      //       ? await convertPdfToImage(file)
      //       : file.type.includes('image')
      //       ? file
      //       : null
      //     : null
      // ) as File | null;
      // if (newFile) {
      //   const thumbnailFile = convertThumbnailFile(file);

      //   newFile &&
      //     setList((prev: any) => ({
      //       ...prev,
      //       otherFiles: [...list, file],
      //       otherFile_thumbnails: [...list, thumbnailFile],
      //     }));
      // }
    }
  };

  const handleEditClick = async (item: File | IFile, index: number) => {
    let document = item;
    if ('url' in item) {
      const cognitoUUID = item.userUUID
        ? item.userUUID
        : uuid
        ? uuid
        : item.uuid;
      const formatUrl: any = await s3GetCognito({
        type,
        imgUrls: [item.url],
        uuid: cognitoUUID,
        userInfo,
      });

      document = {
        ...(document as IFile),
        url: formatUrl[0],
      };
    }

    setEditDocument({item: document, index});
  };

  const handleDownload = async (item: File | IFile) => {
    if ('url' in item) {
      const cognitoUUID = item.userUUID
        ? item.userUUID
        : uuid
        ? uuid
        : item.uuid;
      await handleDownloadClick({
        url: item.url,
        name: item.name,
        userInfo,
        type,
        uuid: cognitoUUID,
      });
    } else {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(item);
      link.download = item.name;

      link.click();
      URL.revokeObjectURL(link.href);
    }
  };

  const handleDeleteClick = (deleteIndex: number) => {
    setList(list.filter((_, index) => deleteIndex !== index));
  };

  const handleEditValue = (value: any) => {
    if (editDocument) {
      setList((prev: any) => {
        let newList = {...prev};
        newList.otherFiles = newList.otherFiles.map(
          (item: any, index: number) =>
            index === editDocument.index ? value : item,
        );
        newList.otherFile_thumbnails = newList.otherFile_thumbnails.map(
          (item: any, index: number) =>
            index === editDocument.index
              ? 'uuid' in value
                ? value
                : convertThumbnailFile(value)
              : item,
        );
        return newList;
      });
      setEditDocument(null);
    }
  };

  return (
    <LabelInputWrap style={{height: 'auto', alignItems: 'flex-start'}}>
      <Label
        style={{flexDirection: 'column', alignItems: 'flex-start'}}
        htmlFor={label}>
        <DefaultText
          text={label}
          textStyle={{
            marginTop: minmax(20),
          }}
        />
        {warningText && (
          <DefaultText
            text={warningText}
            textStyle={{
              fontSize: minmax(14),
              color: colors.darkGray,
              whiteSpace: 'pre',
            }}
          />
        )}
      </Label>
      <div style={{width: '100%'}}>
        {!readOnly && (
          <FileInputButton
            setValue={(file: File) => handleAddFileClick(file)}
            title={buttonText || 'addFilePlus'}
            active={list.length < listMaxLength}
            accept={inputAccept || 'image/jpg, image/jpeg'}
            labelStyle={{width: minmax(180), height: minmax(60)}}
          />
        )}

        {list.length !== 0 ? (
          <DocumentList>
            {list.map((item: File | IFile, index: number) => (
              <DocumentItem key={index}>
                {edit && (
                  <DefaultImageView
                    source="images/common/pencil.png"
                    style={{
                      width: minmax(30),
                      height: minmax(30),
                      cursor: 'pointer',
                      marginRight: 2,
                    }}
                    onClick={() => handleEditClick(item, index)}
                  />
                )}
                <DefaultText
                  text={item.name}
                  textStyle={{
                    textDecoration: 'underline',
                    color:
                      'url' in item && !item.url
                        ? colors.borderGray
                        : colors.black,
                  }}
                />
                {download && 'url' in item && item.url && (
                  <DefaultImageView
                    source="images/common/download_red.png"
                    style={{
                      width: minmax(36),
                      height: minmax(36),
                      cursor: 'pointer',
                    }}
                    onClick={() => handleDownload(item)}
                  />
                )}
                {!readOnly && (
                  <DefaultImageView
                    source="images/common/add_file_delete.png"
                    style={{
                      width: minmax(25),
                      height: minmax(25),
                      cursor: 'pointer',
                      marginLeft: 2,
                    }}
                    onClick={() => {
                      // if ('uuid' in item) {
                      //   setDeleteDocument(
                      //     item.fileUUID ? item.fileUUID : item.uuid,
                      //   );
                      // } else {
                      handleDeleteClick(index);
                      // }
                    }}
                  />
                )}
              </DocumentItem>
            ))}
          </DocumentList>
        ) : (
          readOnly && (
            <DefaultText text="none2" textStyle={{paddingTop: minmax(20)}} />
          )
        )}
      </div>

      {/* {deleteDocument && (
        <CommonModal
          title="checkDeleteDocumentTitle"
          detail="checkDeleteDocumentDetail"
          firstButtonText="delete2"
          secondButtonText="cancel"
          firstButtonStyle={{backgroundColor: colors.borderGray}}
          firstButtonClick={() => {
            handleDeleteClick(deleteDocument);
            setDeleteDocument('');
          }}
          secondButtonClick={() => setDeleteDocument('')}
          closeModal={() => setDeleteDocument('')}
        />
      )} */}
      {/* {editDocument && (
        <ChangeFileNameModal
          img={editDocument.item}
          setModal={(e: boolean) => e === false && setEditDocument(null)}
          setValue={(value: any) => handleEditValue(value)}
        />
      )} */}
    </LabelInputWrap>
  );
};

export default LabelDocument;

const LabelInputWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  /* align-items: flex-start; */

  .profile_img_wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: max(60px, min(6.3vw, 120px));
    height: max(60px, min(6.3vw, 120px));
    overflow: hidden;
    border: 1px solid ${colors.borderGray};
    border-radius: ${minmax(10)};

    .profile_img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

const Label = styled.label<{$isColumn?: boolean}>`
  font-size: ${minmax(20)};
  font-weight: 400;
  /* width: calc(20% - max(8px, min(0.8vw, 16px))); */
  width: 20%;
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  word-break: break-all;
  padding-right: 2%;
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${minmax(12)};
  margin-top: ${minmax(12)};
`;

const DocumentItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
